import {
  IProductDetailById,
  IProductVariation
} from './../models/ProductDetailById';
import { ISubstitute } from './../models/CrossPartDetails';
import { IProductDetails } from '../models/ProductDetails';
import {
  getProductDetailByIds,
  getMediaLink,
  getProductDetailByIdsUnLogged,
  getBasePartNumberDetails,
  getBasePartNumberDetailsUnLogged
} from '../services';
import { MATCHTYPE_SORT, MFR_RECOMMENDED, NON_DIGIKEY } from '../constants';
import {
  UTAG_DATA,
  SOURCE_PART,
  PAGE_SUB_TYPE,
  DRIECT,
  ACTIVE
} from '../constants';
import { IMediaLinkDetail } from '../models/MediaLinkDetail';
import LZString from 'lz-string';
import { ISearchQueryRequest } from '../models/ISearchQueryRequest';
import { ISiteLangObject } from '../models/ISiteLangObject';

export const createUrlSlug = (name: string, toLower: boolean) => {
  const cleaners = {
    ' ': '-',
    '/': '-',
    ',': '',
    '(': '',
    ')': '',
    '+': '',
    '#': '-',
    '%': '-'
  };
  let temp = name;

  for (const [key, value] of Object.entries(cleaners)) {
    temp = temp?.split(key).join(value);
  }
  temp = temp?.split(/-{2,}/).join('-');
  return toLower ? temp?.toLowerCase() : temp;
};

export const generatePartUrl = (
  supplier: string,
  manufacturerPartNumber: string | undefined,
  partId: string
) => {
  let lang = '';
  const langTemp = window.location.pathname.split('/')[1];

  // ! Set default language if url doesn't have langauge param
  if (langTemp.length !== 2) lang = 'en';
  else lang = langTemp;

  const baseUrl = `${window.location.origin}/${lang}`;

  if (manufacturerPartNumber) {
    const supplierUrlSlug = createUrlSlug(supplier, true);

    const manufacturerPartNumberUrlSlug = createUrlSlug(
      manufacturerPartNumber,
      false
    );
    return `${baseUrl}/products/detail/${supplierUrlSlug}/${manufacturerPartNumberUrlSlug}/${partId}`;
  }
  return `${window.location.origin}/404`;
};

export const getUtag = (
  searchTemp: string,
  searchParam: string,
  tableData: ISubstitute[],
  isActivePartApplied: boolean,
  isStockingApplied: boolean,
  isMarketplaceApplied: boolean,
  isExcludeTariffApplied: boolean,
  refPageEvent: string | undefined,
  noCrossPartData: boolean,
  isNoProductFound: boolean,
  isFetch: boolean,
  resultNumber?: number
) => {
  let result = { ...UTAG_DATA.utagData };
  //create part search filter string
  let partSearchFilter = [];
  isActivePartApplied && partSearchFilter.push('Active Parts');
  isStockingApplied && partSearchFilter.push('In Stock');
  isMarketplaceApplied && partSearchFilter.push('Exclude Marketplace Products');
  isExcludeTariffApplied && partSearchFilter.push('Exclude Tariff');

  if (partSearchFilter[0]) {
    const part_search_filter = {
      part_search_filter: partSearchFilter.join(',')
    };
    result = {
      ...result,
      ...part_search_filter
    };
  }

  if (refPageEvent) {
    const ref_page_event = {
      ref_page_event: refPageEvent
    };
    result = {
      ...result,
      ...ref_page_event
    };
  }
  if (searchParam) {
    const manufacturer_part_number = {
      manufacturer_part_number: searchParam,
      part_search_term: searchTemp
    };
    result = {
      ...result,
      page_id: PAGE_SUB_TYPE,
      ...manufacturer_part_number
    };
  }
  let number_results_returned_from_search = {
    "number_results_returned_from_search": resultNumber || 0
  }

  if (tableData[0]) {
    let part_search_results_count;
    if (noCrossPartData || (isNoProductFound && !isFetch)) {
      part_search_results_count = {
        part_search_results_count: 0
      };
    } else {
      part_search_results_count = {
        part_search_results_count: 1
      };
    }

    tableData.forEach((item: ISubstitute) => {
      if (item?.crossType?.type === DRIECT) {
        result = {
          ...result,
          page_id: PAGE_SUB_TYPE
        };
      }
    });
    result = {
      ...result,
      ...part_search_results_count
    };
  } else {
    const part_search_results_count = {
      part_search_results_count: 0
    };
    result = {
      ...result,
      ...part_search_results_count
    };
  }
  result = {
    ...result,
    ...number_results_returned_from_search
  };
  return result;
};

export const convertParameters = (data: any, parameters: any, pins?: any) => {
  const result: any = { ...data };

  parameters?.forEach((item: any) => {
    let name: any = item.parameterText
      .replace(/ /g, '')
      .replace(/[&\\#,+()$~%.'":*?<>{}-]/g, '');
    name = name.charAt(0).toLowerCase() + name.slice(1);
    let value = item.valueText;
    result[name] = value;
  });

  // ! for pin
  pins?.forEach((item: any) => {
    let name: any = `pin${item.number}`;
    let value = item.name;
    result[name] = value;
  });

  return result;
};

export const getStringIncludeValue = (pathname: any) => {
  if (pathname) {
    return (
      pathname.includes('/cross-reference/short/') ||
      pathname.includes('/cross-reference/')
    );
  } else return false;
};

// ! for pin
const containsUndefined = (arr: any) => {
  return arr.includes(undefined);
};

export const getHeadersOfParameters = (
  tableData: any,
  sourcePart: any,
  tabId?: string
) => {
  const headerOfParameters: any = [];
  const productIdList: number[] = tableData.map((a: any) => a.productId);
  // If the part is 'Non Digikey', then set the headers according to the first substitutes.
  const sourcePartData =
    sourcePart?.[0]?.type === NON_DIGIKEY ? tableData : sourcePart;
  if (sourcePartData) {
    sourcePartData[0]?.parameters &&
      sourcePartData[0]?.parameters.forEach((item: any) => {
        let field: string = item.parameterText
          .replace(/ /g, '')
          .replace(/[&\\#,+()$~%.'":*?<>{}-]/g, '');
        field = field.charAt(0).toLowerCase() + field.slice(1);
        let headerName = item.parameterText;
        let parameterId = item.parameterId;
        const obj = { headerName, field, parameterId };
        headerOfParameters.push(obj);
      });

    // ! Check when source have cross
    if (
      !containsUndefined(productIdList) &&
      productIdList.length > 0 &&
      tabId !== 'tabBasePart'
    ) {
      const maxPinLenghtHaveCross = Math.max(
        ...tableData.map(function (item: any) {
          return item.pins ? item.pins.length : 0;
        }),
        0
      );
      for (let i = 1; i <= maxPinLenghtHaveCross; i++) {
        let headerName = `Pin ${i}`;
        let field: string = `pin${i}`;
        const obj = { headerName, field, isPinout: true };
        headerOfParameters.push(obj);
      }
    }
  }

  // ! Check when source don't have cross
  if (tableData.length === 1) {
    const maxPinLenghtNoCross = Math.max(
      ...tableData.map(function (item: any) {
        return item.Pin && item.Pin[0] ? item.Pin[0].pins.length : 0;
      }),
      0
    );
    for (let i = 1; i <= maxPinLenghtNoCross; i++) {
      let headerName = `Pin ${i}`;
      let field: string = `pin${i}`;
      const obj = { headerName, field, isPinout: true };
      headerOfParameters.push(obj);
    }
  }

  return headerOfParameters;
};

export const countWordOfHeader = (headerName: string) => {
  const words = headerName.split(/[\n\s]+/g).filter((word) => word.length > 0);
  
  return words.length;
};

export const getColumns = (
  tableData: any,
  searchedProduct: any,
  tabId?: string,
  columnDefs?: any,
) => {
  const headerOfParameters: any = getHeadersOfParameters(
    tableData,
    searchedProduct,
    tabId
  );

  if (headerOfParameters) {
    const newColumns = [...columnDefs];

    headerOfParameters.forEach((item: any) => {
      const field: string = item.field;
      const headerName: string = item.headerName;
      const parameterId = item.parameterId;

      const cellClassRules: any = {
        'rag-green': (params: any) => {
          // Check if type of part is not source and data of source part was defined 
          if (params.data.crossType.type !== SOURCE_PART && searchedProduct?.length > 0 && searchedProduct[0][field]) {
            return params.data[field] === searchedProduct[0][field];
          }
        },
        'source-part-excel': (params: any) => {
          return params.data.crossType.type === SOURCE_PART;
        }
      };
      const header = addCellRenderForPinColumn(
        field,
        headerName,
        cellClassRules,
        parameterId,
        true
      );
      newColumns.push(header);
    });
    return newColumns;
  }
};

//Sort data
export const sortTableData = (
  tableData: ISubstitute[],
  searchedProduct: any,
  tabId: string
) => {
  if (tabId !== 'tabBasePart' && tableData) {
    let result: ISubstitute[] = [];
    const headerOfParameters: any = getHeadersOfParameters(
      tableData,
      searchedProduct,
      tabId
    );
    result = tableData;
    result.forEach((item: any) => {
      if (item) {
        let score = 0;
        headerOfParameters &&
          headerOfParameters.forEach((header: any) => {
            const field: string = header.fieldName;
            if (item[field] === searchedProduct[0][field]) score += 1;
          });
        item['score'] = score;
      }
    });

    result.sort((a: any, b: any) => {
      return b.score - a.score;
    });
    result = [
      ...tableData.filter(({ partStatus }) => partStatus === ACTIVE),
      ...tableData.filter(({ partStatus }) => partStatus !== ACTIVE)
    ];
    return defaultSortCrossTypeData(result, tabId);
  }
  return tableData;
};

export const convertSourcePartDetail = async (
  searchedData: IProductDetails,
  index: number
) => {
  let result: any = {};
  const parameters = convertProductStatusToParameters(searchedData.products[index].productStatus, searchedData.products[index].parameters);
  result.productId = searchedData.products[index].productVariations[0]?.masterProductId;
  result.productClass = searchedData.products[index].productClass;
  result.isNonStock = searchedData.products[index].isNonStock;
  result.partStatusId = searchedData.products[index].productStatus.id;
  result.partStatus = searchedData.products[index].productStatus?.status;
  result.parameters = parameters;
  result.category = searchedData.products[index].category;
  result.manufacturerProductNumber =
    searchedData.products[index].manufacturerProductNumber;
  result.productVariations = searchedData.products[index].productVariations;
  result.crossType = {
    crossDescription: 'This is a source part',
    crossName: SOURCE_PART,
    type: SOURCE_PART
  };
  result.primaryPhotoUrl = searchedData.products[index].primaryPhoto?.url;
  result.vendor = {
    key: searchedData.products[index].manufacturer?.id,
    value: searchedData.products[index].manufacturer?.name
  };
  result.catalogDescription = searchedData.products[index].description?.catalog;
  result.primaryDatasheetUrl =
    searchedData.products[index]?.primaryDatasheet?.url;
  result.rohsStatus = searchedData.products[index].rohsStatus;
  result.quantityAvailable = searchedData.products[index].quantityAvailable;
  result.manufacturerPublicQuantity =
    searchedData.products[index].manufacturerPublicQuantity;
  result.seriesName = searchedData.products[index].series?.name;
  result.seriesId = searchedData.products[index].series?.id;
  result.url = generatePartUrl(
    searchedData.products[index].manufacturer.name,
    searchedData.products[index].manufacturerProductNumber,
    searchedData.products[index].productVariations[0]?.productId.toString()
  );

  const pinList = searchedData.products[index].Pin;
  const pinParse = pinList && pinList[0] ? pinList[0].pins : [];

  result = await convertParameters(result, parameters, pinParse);

  let resultArray: ISubstitute[] = [];
  resultArray.push(result);
  return await Promise.all(resultArray);
};

export const getAdditionalData = async (
  tableData: ISubstitute[],
  sourceDetails: IProductDetailById[],
  manufacturerPartNumber: string,
  manufacturerId: number,
  index: number,
  tabId?: string,
  lang?: string,
    site?: string,
    currency?: string
) => {
  let additionalDataCalls: any = [];

  // ! Check tab to use API
  if (tabId !== 'tabBasePart') {
    //! Get all product ids
    const productIdList: number[] = tableData.map((a) => a.productId);

    const productDetailList: IProductDetailById[] = await getProductDetailByIds(
      productIdList,
      manufacturerPartNumber,
      manufacturerId,
      lang,
        site,
        currency
    );

    tableData.forEach((item) => {
      const productDetail: IProductDetailById | undefined =
        productDetailList.find((element) => {
          return (
            element.manufacturerProductNumber ===
              item.manufacturerProductNumber &&
            element.productVariations.find(
              (e) => e.productId === item.productId
            )
          );
        });

      const initProdVars = productDetail?.productVariations;
      if (initProdVars) {
        // Get product is marketplace in list productDetail
        const marketPlaceProdVars = initProdVars.filter((x) => x.isMarketPlace);
        // Get product is NOT marketplace in list productDetail
        let productVariations = initProdVars.filter((x) => !x.isMarketPlace);

        // Add product marketplace into list cross result
        if (marketPlaceProdVars.length > 0) {
          const productIdCompared = marketPlaceProdVars[0]?.productId;
          const quantityAvailable = productDetail?.quantityAvailable || 0;
          const parameters = convertProductStatusToParameters(productDetail.productStatus, productDetail.parameters);
          const category = productDetail?.category;
          const productClass = productDetail?.productClass;
          const seriesName = productDetail?.series.name;
          const manufacturerPublicQuantity =
            productDetail?.manufacturerPublicQuantity;
          const rohsStatus = productDetail?.rohsStatus;
          const manufacturerId = productDetail?.manufacturer.id;
          const productVariations = marketPlaceProdVars;
          const pins =
            productDetail && productDetail.Pin && productDetail.Pin[0]
              ? productDetail.Pin[0].pins
              : [];

          const isMarketPlace = true;
          const url = generatePartUrl(
            marketPlaceProdVars[0]?.supplier?.name,
            productDetail?.manufacturerProductNumber,
            marketPlaceProdVars[0]?.productId.toString()
          );

          item = convertParameters(item, parameters, pins);
          const substitute = {
            ...item,
            productIdCompared,
            quantityAvailable,
            parameters,
            productVariations,
            seriesName,
            manufacturerPublicQuantity,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            pins,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }

        // Add product is NOT marketplace into list cross result
        if (productVariations.length > 0) {
          let quantityAvailable = 0;
          for (let value of productVariations) {
            quantityAvailable = quantityAvailable + value.quantityAvailable;
          }
          const productIdCompared = productVariations[0]?.productId;
          const parameters = convertProductStatusToParameters(productDetail.productStatus, productDetail.parameters);
          const manufacturerPublicQuantity =
            productDetail?.manufacturerPublicQuantity;
          const category = productDetail?.category;
          const productClass = productDetail?.productClass;
          const seriesName = productDetail?.series.name;
          const rohsStatus = productDetail?.rohsStatus;
          const manufacturerId = productDetail?.manufacturer.id;
          const isMarketPlace = false;
          const pins =
            productDetail && productDetail.Pin && productDetail.Pin[0]
              ? productDetail.Pin[0].pins
              : [];
          const url = generatePartUrl(
            productVariations[0]?.supplier?.name,
            productDetail?.manufacturerProductNumber,
            productVariations[0]?.productId.toString()
          );
          item = convertParameters(item, parameters, pins);
          let substitute;
          substitute = {
            ...item,
            productIdCompared,
            quantityAvailable,
            parameters,
            productVariations,
            manufacturerPublicQuantity,
            seriesName,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            pins,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }
      }
    });
  } else {
    // ! Get Products Details For Tab Basepart
    const productDetailListOfBasePart: IProductDetails =
      await getBasePartNumberDetails(
        sourceDetails[index].manufacturer.id,
        sourceDetails[index].baseProductNumber.id,
        lang,
        site,
        currency
      );

    const tableBasePart: IProductDetailById[] =
      productDetailListOfBasePart.products;

    // ! Map param to field object to show data
    tableBasePart.forEach((item) => {
      const sourceDetails: IProductDetailById | undefined = item;

      const initProdVarsBasePart = sourceDetails?.productVariations;

      if (initProdVarsBasePart) {
        const marketPlaceProdVars = initProdVarsBasePart.filter(
          (x) => x.isMarketPlace
        );
        let productVariations = initProdVarsBasePart.filter(
          (x) => !x.isMarketPlace
        );

        if (marketPlaceProdVars.length > 0) {
          const productIdCompared = marketPlaceProdVars[0]?.productId;
          const quantityAvailable = sourceDetails?.quantityAvailable || 0;
          const productId = sourceDetails?.productVariations[0].masterProductId;
          const primaryDatasheetUrl = sourceDetails?.primaryDatasheet?.url;
          const primaryPhotoUrl = sourceDetails?.primaryPhoto?.url;
          const catalogDescription = sourceDetails?.description?.catalog;
          const category = sourceDetails?.category;
          const productClass = sourceDetails?.productClass;
          const manufacturerPublicQuantity =
            sourceDetails?.manufacturerPublicQuantity;
          const parameters =  convertProductStatusToParameters(sourceDetails.productStatus, sourceDetails.parameters);
          const seriesName = sourceDetails?.series.name;
          const rohsStatus = sourceDetails?.rohsStatus;
          const manufacturerId = sourceDetails?.manufacturer.id;
          const productVariations = marketPlaceProdVars;
          const isMarketPlace = true;
          const url = generatePartUrl(
            marketPlaceProdVars[0]?.supplier?.name,
            sourceDetails?.manufacturerProductNumber,
            marketPlaceProdVars[0]?.productId.toString()
          );
          item = convertParameters(item, parameters);
          const substitute = {
            ...item,
            productIdCompared,
            crossType: { crossName: '', crossDescription: '', type: '' },
            productId,
            primaryDatasheetUrl,
            primaryPhotoUrl,
            catalogDescription,
            quantityAvailable,
            parameters,
            manufacturerPublicQuantity,
            productVariations,
            seriesName,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            category,
            productClass
          };
          additionalDataCalls.push(substitute);
        }

        if (productVariations.length > 0) {
          let quantityAvailable = 0;
          for (let value of productVariations) {
            quantityAvailable = quantityAvailable + value.quantityAvailable;
          }

          const productIdCompared = productVariations[0]?.productId;
          const productId = sourceDetails?.productVariations[0].masterProductId;
          const primaryDatasheetUrl = sourceDetails?.primaryDatasheet?.url;
          const primaryPhotoUrl = sourceDetails?.primaryPhoto?.url;
          const catalogDescription = sourceDetails?.description?.catalog;
          const category = sourceDetails?.category;
          const productClass = sourceDetails.productClass;
          const parameters =  convertProductStatusToParameters(sourceDetails.productStatus, sourceDetails.parameters);
          const seriesName = sourceDetails?.series.name;
          const rohsStatus = sourceDetails?.rohsStatus;
          const manufacturerId = sourceDetails?.manufacturer.id;
          const isMarketPlace = false;
          const manufacturerPublicQuantity =
            sourceDetails?.manufacturerPublicQuantity;
          const url = generatePartUrl(
            productVariations[0]?.supplier?.name,
            sourceDetails?.manufacturerProductNumber,
            productVariations[0]?.productId.toString()
          );
          item = convertParameters(item, parameters);
          const substitute = {
            ...item,
            productIdCompared,
            crossType: { crossName: '', crossDescription: '', type: '' },
            primaryDatasheetUrl,
            primaryPhotoUrl,
            catalogDescription,
            productId,
            quantityAvailable,
            parameters,
            manufacturerPublicQuantity,
            productVariations,
            seriesName,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            category,
            productClass
          };
          additionalDataCalls.push(substitute);
        }
      }
    });
  }

  return Promise.all(additionalDataCalls);
};

export const populateMediaLink = async (
  tableData: any,
  sourceProductId: number,
  lang?: string,
  site?: string
) => {
  //Filter products with MFR Recommended
  let pcnMedia: any;
  const sourceProductIdList: number[] = [sourceProductId];
  const sourcePartMediaLink: IMediaLinkDetail[] = await getMediaLink(
    sourceProductIdList,
    lang,
    site
  );

  try {
    pcnMedia = sourcePartMediaLink[0]?.media.find((element) => {
      if (element.mediaType === 'PCN Obsolescence/ EOL') {
        return element.url;
      }
      return null;
    });
  } catch {
    console.log(sourceProductId);
  }

  const additionalDataCallsTest = tableData.map(async (item: any) => {
    if (item.crossType.type === MFR_RECOMMENDED && pcnMedia) {
      return {
        ...item,
        pcnMedia
      };
    } else {
      return item;
    }
  });

  return Promise.all(additionalDataCallsTest);
};

export const validateProducDetail = (productDetails: IProductDetails) => {
  if (productDetails.products.length > 1) {
    for (var i = 0; i < productDetails.products.length; i++) {
      let isNotCorePart = false;
      for (
        var x = 0;
        x < productDetails.products[i].productVariations.length;
        x++
      ) {
        if (
          productDetails.products[i].productVariations[x].supplier.id !==
            productDetails.products[i].manufacturer.id &&
          productDetails.products[i].productVariations[x].isMarketPlace
        ) {
          isNotCorePart = true;
        } else {
          isNotCorePart = false;
        }
      }
      if (isNotCorePart) {
        productDetails.products.splice(i, 1);
      }
    }
  }

  //Set source part as first variation
  const sourcePartData = chooseSourcePart(productDetails, 0);
  if (sourcePartData) {
    productDetails.products[0].productVariations.sort(function (x, y) {
      return x === sourcePartData ? -1 : y === sourcePartData ? 1 : 0;
    });
  }
};

export const chooseSourcePart = (
  productDetails: IProductDetails,
  index: number
) => {
  let productData: IProductDetailById = productDetails.products[index];
  let corePart;
  let marketplacePart;
  let resultPart;
  try {
    for (let variation of productData.productVariations) {
      if (
        variation.masterProductId === variation.productId &&
        !variation.isMarketPlace
      ) {
        corePart = variation;
      }
      if (
        variation.masterProductId === variation.productId &&
        variation.isMarketPlace
      ) {
        marketplacePart = variation;
      }
    }
  } catch {
    console.log(productData);
  }

  if (corePart) {
    resultPart = corePart;
  } else {
    marketplacePart && (resultPart = marketplacePart);
  }

  return resultPart;
};
//Default sort by match type

const customSortByMatchType = (data: any[], customArray: string[]) => {
  return data.sort((a: any, b: any) => {
    if (
      customArray.indexOf(a.crossType.type) <=
      customArray.indexOf(b.crossType.type)
    ) {
      return -1;
    } else {
      return 1;
    }
  });
};

export const defaultSortCrossTypeData = (
  tableData: ISubstitute[],
  tabId: string
) => {
  let nonActiveData: any = [];
  let withActiveData: any = [];
  let finalData: any = [];
  if (!tableData) {
    return tableData;
  }
  if (tabId !== 'tabBasePart') {
    //Part Status is active
    withActiveData = tableData.filter(
      (x) => x.parameters?.[0]?.['valueId'] === '0'
    );
    if (withActiveData.length > 0) {
      withActiveData = customSortByMatchType(withActiveData, MATCHTYPE_SORT);
    }
    //Part Status not active
    nonActiveData = tableData.filter(
      (x) => x.parameters?.[0]?.['valueId'] !== '0'
    );
    if (nonActiveData.length > 0) {
      nonActiveData = customSortByMatchType(nonActiveData, MATCHTYPE_SORT);
    }
    finalData = withActiveData.concat(nonActiveData);
    return finalData;
  } else {
    withActiveData = tableData.filter(
      (x) => x.parameters?.[0]?.['valueId'] === '0'
    );
    nonActiveData = tableData.filter(
      (x) => x.parameters?.[0]?.['valueId'] !== '0'
    );

    return withActiveData.concat(nonActiveData);
  }
};

// ! BASE PART
//Get Additional Data Unlogged
export const getAdditionalDataUnLogged = async (
  tableData: ISubstitute[],
  sourceDetails: IProductDetailById[],
  index: number,
  tabId?: string,
  lang?: string,
    site?: string,
    currency?: string,
    manufacturerPartNumber?: string,
    manufacturerId?: number
) => {
  let additionalDataCalls: any = [];

  //! Get parameters object to encrypt data
  const parameters: any = sourceDetails[index]?.parameters;

  // ! Check tab to use API
  if (tabId !== 'tabBasePart') {
    //! Get all product ids
    const productIdList: number[] = tableData.map((a) => a.productId);

    const productDetailList: IProductDetailById[] =
      await getProductDetailByIdsUnLogged(
        productIdList,
        parameters,
        lang,
          site,
          currency,
          manufacturerPartNumber,
          manufacturerId
      );

    tableData.forEach((item) => {
      const sourceDetailsInBlock: IProductDetailById | undefined =
        productDetailList.find((element) => {
          return (
            element.manufacturerProductNumber ===
              item.manufacturerProductNumber &&
            element.productVariations.find(
              (e) => e.productId === item.productId
            )
          );
        });
      const initProdVars = sourceDetailsInBlock?.productVariations;

      if (initProdVars) {
        // Get product is marketplace in list productDetail
        const marketPlaceProdVars = initProdVars.filter((x) => x.isMarketPlace);
        // Get product is NOT marketplace in list productDetail
        let productVariations = initProdVars.filter((x) => !x.isMarketPlace);

        // Add product marketplace into list cross result
        if (marketPlaceProdVars.length > 0) {
          const productIdCompared = marketPlaceProdVars[0]?.productId;
          const quantityAvailable =
            sourceDetailsInBlock?.quantityAvailable || 0;
          const parameters = convertProductStatusToParameters(sourceDetailsInBlock.productStatus, sourceDetailsInBlock.parameters);
          const category = sourceDetailsInBlock?.category;
          const productClass = sourceDetailsInBlock?.productClass;
          const seriesName = sourceDetailsInBlock?.series.name;
          const rohsStatus = sourceDetailsInBlock?.rohsStatus;
          const manufacturerPublicQuantity =
            sourceDetailsInBlock?.manufacturerPublicQuantity;
          const manufacturerId = sourceDetailsInBlock?.manufacturer.id;
          const productVariations = marketPlaceProdVars;
          const isMarketPlace = true;
          const url = generatePartUrl(
            marketPlaceProdVars[0]?.supplier?.name,
            sourceDetailsInBlock?.manufacturerProductNumber,
            marketPlaceProdVars[0]?.productId.toString()
          );

          const pins =
            sourceDetailsInBlock &&
            sourceDetailsInBlock.Pin &&
            sourceDetailsInBlock.Pin[0]
              ? sourceDetailsInBlock.Pin[0].pins
              : [];
          item = convertParameters(item, parameters, pins);
          const substitute = {
            ...item,
            productIdCompared,
            quantityAvailable,
            parameters,
            productVariations,
            seriesName,
            manufacturerPublicQuantity,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            pins,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }
        // Add product is NOT marketplace into list cross result
        if (productVariations.length > 0) {
          let quantityAvailable = 0;
          for (let value of productVariations) {
            quantityAvailable = quantityAvailable + value.quantityAvailable;
          }

          const productIdCompared = productVariations[0]?.productId;
          const parameters = convertProductStatusToParameters(sourceDetailsInBlock.productStatus, sourceDetailsInBlock.parameters);
          const seriesName = sourceDetailsInBlock?.series.name;
          const manufacturerPublicQuantity =
            sourceDetailsInBlock?.manufacturerPublicQuantity;
          const category = sourceDetailsInBlock?.category;
          const productClass = sourceDetailsInBlock?.productClass;
          const rohsStatus = sourceDetailsInBlock?.rohsStatus;
          const manufacturerId = sourceDetailsInBlock?.manufacturer.id;
          const isMarketPlace = false;
          const url = generatePartUrl(
            productVariations[0]?.supplier?.name,
            sourceDetailsInBlock?.manufacturerProductNumber,
            productVariations[0]?.productId.toString()
          );
          const pins =
            sourceDetailsInBlock &&
            sourceDetailsInBlock.Pin &&
            sourceDetailsInBlock.Pin[0]
              ? sourceDetailsInBlock.Pin[0].pins
              : [];
          item = convertParameters(item, parameters, pins);
          const substitute = {
            ...item,
            productIdCompared,
            quantityAvailable,
            parameters,
            productVariations,
            seriesName,
            rohsStatus,
            manufacturerPublicQuantity,
            manufacturerId,
            isMarketPlace,
            url,
            pins,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }
      }
    });
  } else {
    // ! Get Products Details For Tab Basepart
    const productDetailListOfBasePart: IProductDetails =
      await getBasePartNumberDetailsUnLogged(
        sourceDetails[index].manufacturer.id,
        sourceDetails[index].baseProductNumber.id,
        parameters,
        lang,
          site,
          currency
      );

    const tableBasePart: IProductDetailById[] =
      productDetailListOfBasePart.products;

    // ! Map param to field object to show data
    tableBasePart.forEach((item) => {
      const sourceDetailsInBlock: IProductDetailById | undefined = item;

      const initProdVars = sourceDetailsInBlock?.productVariations;

      if (initProdVars) {
        // Get product is marketplace in list productDetail
        const marketPlaceProdVars = initProdVars.filter((x) => x.isMarketPlace);
        // Get product is NOT marketplace in list productDetail
        let productVariations = initProdVars.filter((x) => !x.isMarketPlace);

        // Add product marketplace into list cross result
        if (marketPlaceProdVars.length > 0) {
          const quantityAvailable =
            sourceDetailsInBlock?.quantityAvailable || 0;
          const productIdCompared = marketPlaceProdVars[0]?.productId;
          const productId =
            sourceDetailsInBlock?.productVariations[0].masterProductId;
          const primaryDatasheetUrl =
            sourceDetailsInBlock?.primaryDatasheet?.url;
          const category = sourceDetailsInBlock?.category;
          const productClass = sourceDetailsInBlock?.productClass;
          const primaryPhotoUrl = sourceDetailsInBlock?.primaryPhoto?.url;
          const catalogDescription = sourceDetailsInBlock?.description?.catalog;
          const parameters = convertProductStatusToParameters(sourceDetailsInBlock.productStatus, sourceDetailsInBlock.parameters);
          const seriesName = sourceDetailsInBlock?.series.name;
          const rohsStatus = sourceDetailsInBlock?.rohsStatus;
          const manufacturerPublicQuantity =
            sourceDetailsInBlock?.manufacturerPublicQuantity;
          const manufacturerId = sourceDetailsInBlock?.manufacturer.id;
          const productVariations = marketPlaceProdVars;
          const isMarketPlace = true;
          const url = generatePartUrl(
            marketPlaceProdVars[0]?.supplier?.name,
            sourceDetailsInBlock?.manufacturerProductNumber,
            marketPlaceProdVars[0]?.productId.toString()
          );

          item = convertParameters(item, parameters);
          const substitute = {
            ...item,
            productIdCompared,
            crossType: { crossName: '', crossDescription: '', type: '' },
            productId,
            primaryDatasheetUrl,
            primaryPhotoUrl,
            catalogDescription,
            quantityAvailable,
            parameters,
            productVariations,
            seriesName,
            manufacturerPublicQuantity,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }
        // Add product is NOT marketplace into list cross result
        if (productVariations.length > 0) {
          let quantityAvailable = 0;
          for (let value of productVariations) {
            quantityAvailable = quantityAvailable + value.quantityAvailable;
          }
          const productIdCompared = productVariations[0]?.productId;
          const productId =
            sourceDetailsInBlock?.productVariations[0].masterProductId;
          const primaryDatasheetUrl =
            sourceDetailsInBlock?.primaryDatasheet?.url;
          const primaryPhotoUrl = sourceDetailsInBlock?.primaryPhoto?.url;
          const catalogDescription = sourceDetailsInBlock?.description?.catalog;
          const category = sourceDetailsInBlock?.category;
          const productClass = sourceDetailsInBlock?.productClass;
          const parameters = convertProductStatusToParameters(sourceDetailsInBlock.productStatus, sourceDetailsInBlock.parameters);
          const seriesName = sourceDetailsInBlock?.series.name;
          const manufacturerPublicQuantity =
            sourceDetailsInBlock?.manufacturerPublicQuantity;
          const rohsStatus = sourceDetailsInBlock?.rohsStatus;
          const manufacturerId = sourceDetailsInBlock?.manufacturer.id;
          const isMarketPlace = false;
          const url = generatePartUrl(
            productVariations[0]?.supplier?.name,
            sourceDetailsInBlock?.manufacturerProductNumber,
            productVariations[0]?.productId.toString()
          );

          item = convertParameters(item, parameters);
          const substitute = {
            ...item,
            productIdCompared,
            crossType: { crossName: '', crossDescription: '', type: '' },
            primaryDatasheetUrl,
            primaryPhotoUrl,
            catalogDescription,
            productId,
            quantityAvailable,
            parameters,
            manufacturerPublicQuantity,
            productVariations,
            seriesName,
            rohsStatus,
            manufacturerId,
            isMarketPlace,
            url,
            category,
            productClass
          };

          additionalDataCalls.push(substitute);
        }
      }
    });
  }

  return Promise.all(additionalDataCalls);
};

export const wrapReturnStringForUnitTest = (text: string | null) => {
  if (text) {
    return text;
  } else {
    return null;
  }
};

export const wrapDataReturnDataForUnitTest = (data: any) => {
  return data;
};

//Get Column unlogged
export const getColumnsUnLogged = (
  tableData: any,
  searchedProduct: any,
  tabId?: string,
  columnDefs?: any
) => {
  const headerOfParameters: any = getHeadersOfParameters(
    tableData,
    searchedProduct,
    tabId
  );

  if (headerOfParameters) {
    const newColumns = [...columnDefs];

    headerOfParameters.forEach((item: any) => {
      const field: string = item.field;
      const headerName: string = item.headerName;
      const parameterId = item.parameterId;
      const cellClassRules: any = {
        'rag-green': (params: any) => {
           // Check if type of part is not source and data of source part was defined 
          if (params.data.crossType.type !== SOURCE_PART && searchedProduct?.length > 0 && searchedProduct[0][field]) {
            // with param value = Encryption of 1 || = SOURCE.PARAM[i]||, 1 meansparam value of cross = param value of source in the same params column.
            return (
              params.data[field] ===
                'ng3I/uQttnHX1/EFFjFj3Rv/nNRfE2upZOenQbEsXPE=' ||
              (params.data[field] === searchedProduct[0][field] &&
                params.data[field] !==
                  '+q2zY1iLQAr9ZPfNgUgSS4TglTYCWQaieY5LMLPH5AM=')
            );
          }
        },
        'hide-value': (params: any) => {
          if (params.data.crossType.type !== SOURCE_PART && searchedProduct) {
            // with param value = Encryption of 1 || 1 mean param value of cross = param value of source in the same params column, 0 is different
            return (
              params.data[field] ===
                'ng3I/uQttnHX1/EFFjFj3Rv/nNRfE2upZOenQbEsXPE=' ||
              params.data[field] ===
                '+q2zY1iLQAr9ZPfNgUgSS4TglTYCWQaieY5LMLPH5AM='
            );
          }
        },
        'source-part-excel': (params: any) => {
          return params.data.crossType.type === SOURCE_PART;
        }
      };
      const header = addCellRenderForPinColumn(
        field,
        headerName,
        cellClassRules,
        parameterId,
        false
      );
      newColumns.push(header);
    });
    return newColumns;
  }
};

export const updateUrlUtil = (shareUrl: string, key: string, value: string) => {
  let urlParamsClickedTab =
    shareUrl === ''
      ? new URLSearchParams()
      : new URLSearchParams(new URL('', shareUrl).search);
  const decodedParams = new URLSearchParams(
    '?' +
      LZString.decompressFromEncodedURIComponent(urlParamsClickedTab.get('s')!)
  );
  decodedParams.set(key, value);
  return decodedParams;
};

export const resetSearchRequest = (request: ISearchQueryRequest) => {
  let resetSearchRequest: ISearchQueryRequest = {};
  if (Object.keys(request).length > 0) {
    Object.keys(request).forEach((iParam: string) => {
      if (iParam === 'searchOptions') {
        resetSearchRequest = {
          ...resetSearchRequest,
          searchOptions: request.searchOptions
        };
      }
      if (iParam === 'marketPlaceFilter') {
        resetSearchRequest = {
          ...resetSearchRequest,
          marketPlaceFilter: request.marketPlaceFilter
        };
      }
    });
  }
  return resetSearchRequest;
};
export const loadSearchBarAndDropDownMenu = () => {
  window.addEventListener('DOMContentLoaded', function () {
    (window as any).searchBarModule?.();
  });
  var DOMContentLoaded_event = document.createEvent('Event');
  DOMContentLoaded_event.initEvent('DOMContentLoaded', true, true);
  window.document.dispatchEvent(DOMContentLoaded_event);
  window.scrollTo(0, 0);
};

//Find CorePart and return CorePart Suplier name
export const getSupplierNameCorePart = (
  productVariations: IProductVariation[]
) => {
  let corePart = null;
  let marketplacePart = null;
  let supplierNameCorePart: string = '';
  try {
    for (let variation of productVariations) {
      if (
        variation.masterProductId === variation.productId &&
        !variation.isMarketPlace
      ) {
        corePart = variation;
      }
      if (
        variation.masterProductId === variation.productId &&
        variation.isMarketPlace
      ) {
        marketplacePart = variation;
      }
    }
  } catch {
    console.log(productVariations);
  }

  if (corePart) {
    supplierNameCorePart = corePart.supplier.name;
  } else if (marketplacePart) {
    supplierNameCorePart = marketplacePart.supplier.name;
  } else {
    supplierNameCorePart = productVariations[0].supplier.name;
  }

  return supplierNameCorePart;
};


export const removeDuplicates = (arr: any, key: string) => {
  return arr.filter(function (item: any, pos: any, a: any) {
    return (
      a
        .map(function (mapItem: any) {
          return mapItem[key];
        })
        .indexOf(item[key]) === pos
    );
  });
};

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
  } catch {
    return false;
  }
  return true;
};

//Filter part with status Active
export const filterActivePart = (data: ISubstitute[]) => {
  return data?.filter((item: any) => item.parameters?.[0]?.['valueId'] === '0');
};

//Exclude all marketlace parts
export const filterMarketplacePart = (data: ISubstitute[]) => {
  return data?.filter((item: any) => !item.isMarketPlace);
};

//Exclude all non-stocking parts
export const filterStockingPart = (data: ISubstitute[]) => {
  return data?.filter(
    (item: any) =>
      !(
        item.manufacturerPublicQuantity === 0 && item.quantityAvailable === 0
      ) &&
      !(item.productVariations[0].quantityAvailable === 0 && item.isMarketPlace)
  );
};

export const filterExcludeTariff = (data: ISubstitute[]) => {
  return data?.filter((item: any) =>
    !item.productVariations?.some(
    (x: any) =>
      x.priceBreaksIncludeTariff.length > 0
    )
  );
};

export const getMaxLengthCharacterInWords = (headerName: string) => {
  let words = headerName.split(' ');
  let maxLength = 0;
  for (let word of words) {
    if (word.length > maxLength) {
      maxLength = word.length;
    }
  }
  return maxLength;
};

export const addCellRenderForPinColumn = (
  field: string,
  headerName: string,
  cellClassRules: any,
  parameterId: any,
  isLogged: boolean
) => {
  let header: any;

  const maxLengthCharacter = getMaxLengthCharacterInWords(headerName);

  const numberOfWord = countWordOfHeader(headerName);

   // has sort icon 5.5 = 7.5
   const width =
   (maxLengthCharacter * 5.5 )+ 40 + numberOfWord * 2;

  header = {
    field,
    headerName,
    cellClassRules,
    parameterId
  };

  header = {
    ...header,
    minWidth: width,
    width: width,
    headerTooltip: headerName
  };

  // Disable filter when not logged in and not partStatus headerName
  if(!isLogged && parameterId !== 1989) {
    header = {
      ...header,
      suppressMenu: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    };
  }

  if (field.startsWith('pin')) {
    header = {
      ...header,
      cellRenderer: 'pinNameCellRender',
      minWidth: width + 25,
      width: width + 25
    };
  }  

  if(parameterId===2066 || parameterId===69 || parameterId===16 || parameterId===1291){
    header = {
      ...header,
      minWidth: width + 45,
      width: width + 45
    };
  }

  return header;
};

export const convertProductStatusToParameters = (productStatus: any, parameters: any) => {
  const partStatusObject = {
    parameterId: 1989,
    parameterText: "Part Status",
    parameterType: "String",
    valueId: productStatus.id.toString(),
    valueText: productStatus.status
  }

  parameters.unshift(partStatusObject);

  return parameters;
}

export const validateForChineseLanguage = (siteLangObject: ISiteLangObject) =>{
  if(siteLangObject.site === 'cn' && siteLangObject.lang === 'zh'){
    return true;
  }
  return false;
}